import React, {useEffect, useReducer, useRef} from 'react';
import Input from 'Components/search/subcomponents/input';
import Tracking from '../../../../clientlibs/clientlib-publish/src/js/tracking';

const initialState = {
    searchInputVisible: undefined,
    query: '',
    autocompleteQuery: '',
    autocompleteResultProducts: [],
    autocompleteResultPages: [],
};

/**
 * Return current query (aka search term)
 *
 * @returns query as {string} or empty {string} if not available
 */
const getQuery = () => {
    const value = /[?&]q=([^&#]*)/.exec(document.location.search);
    if (value == null) {
        return '';
    }
    return decodeURI(value[1]) || '';
};

/**
 * Add search term (from query string) to initial state
 *
 * @param initState The current state
 *
 * @returns new state
 */
const init = (initState) => {
    const initQuery = getQuery();
    return {
        ...initState,
        query: initQuery,
    };
};

const fetchAutocomplete = async (url, query) => {
    const autoCompleteUrl = `${url}?q=${query}`;
    const response = await fetch(autoCompleteUrl);
    return response.json();
};

/**
 * Reducer handling given action
 *
 * @param state The current state
 * @param action The action to execute
 *
 * @returns new state
 */
const reducer = (state, action) => {
    switch (action.type) {
        // toggle the search input
        case 'toggleSearch':
            return {
                ...state,
                'searchInputVisible': action.payload?.value || state.searchFormElementClicked || (action.payload?.value === undefined && !state.searchInputVisible),
                'searchFormElementClicked': false,
            };
        // show the autocomplete box of the search input
        case 'showAutocomplete':
            return {
                ...state,
                'autocompleteResultProducts': action.payload.autocompleteResultProducts,
                'autocompleteResultPages': action.payload.autocompleteResultPages,
            };
        // show the autocomplete results
        case 'getAutocompleteResults':
            return {
                ...state,
                'autocompleteQuery': action.payload.autocompleteQuery,
            };
        default:
            return state;
    }
};

/**
 * The container of the header search
 * @param props properties
 * @returns {JSX.Element}
 * @constructor
 */
const Container = ({props}) => {
    const [state, dispatch] = useReducer(reducer, initialState, init);
    const linkRef = useRef();

    const closeSearchHandler = () => {
        dispatch({
            type: 'toggleSearch',
            payload: {
                ...state,
                value: false,
            },
        });
    };

    const toggleSearchHandler = () => {
        dispatch({type: 'toggleSearch'});
    };

    useEffect(() => {
        // executes a request to get the autocomplete results for the current input
        if (state.autocompleteQuery) {
            fetchAutocomplete(props.autocompleteUrl, state.autocompleteQuery)
                .then((json) => {
                    dispatch({
                        type: 'showAutocomplete',
                        payload:
                            {
                                ...state,
                                'autocompleteResultProducts': json.productEntries,
                                'autocompleteResultPages': json.searchEntries,
                            },
                    });
                })
                .catch(() => {
                    dispatch({type: 'setError'});
                });
        } else {
            dispatch({
                type: 'showAutocomplete',
                payload:
                    {
                        ...state,
                        'autocompleteResults': [],
                    },
            });
        }

        window.addEventListener('closeSearch', closeSearchHandler);

        window.addEventListener('toggleSearch', toggleSearchHandler);

        window.addEventListener('click', () => {
            if (window.innerWidth < 640) {
                return;
            }
            document.querySelector('.brand-bar a.magenta-icon-close')?.classList.add('hidden');
            dispatch({
                type: 'toggleSearch',
                payload: {
                    ...state,
                    value: false,
                },
            });
        });
        linkRef?.current?.parentElement.addEventListener('click', (e) => {
            e.stopPropagation();
        });

        // cleanup code
        return () => {
            window.removeEventListener('closeSearch', closeSearchHandler);
            window.removeEventListener('toggleSearch', toggleSearchHandler);
        };
    }, [state.autocompleteQuery]);

    /**
     * Changes the search query and calls the search result page
     * @param query the query from user input
     */
    const changeQueryAndSearch = (query) => {
        let urlParams = document.location.search;
        if (urlParams.indexOf('f=') > -1) {
            urlParams = urlParams
                .replace(/&f=search:website\/consumer\/5-faq/g, '')
                .replace(/&f=search:website\/business\/7-faq/g, '');
        }
        if (urlParams.indexOf('?') > -1) {
            if (urlParams.indexOf('q=') > -1) {
                urlParams = urlParams.replace(/q=([^&#]*)/, `q=${query}`);
            } else {
                urlParams = `${urlParams}&q=${query}`;
            }
        } else {
            urlParams = `?q=${query}`;
        }
        document.location.href = props.searchResultPage + urlParams;
    };

    /**
     * Gets the autocomplete box
     * @param query the query typed in by the user
     */
    const getAutocomplete = (query) => {
        if (query.length > 1) {
            dispatch({
                type: 'getAutocompleteResults',
                payload: {'autocompleteQuery': query},
            });
        }
    };

    /**
     * Toggle 'hidden' state in order to open/close search input field
     */
    const toggleSearch = (e) => {
        if ((e?.key && e.key !== 'Enter') || (e?.keyCode && e.keyCode !== 13)) return;
        Tracking.trackSearchIconClick();
        const closeSearchEvent = new CustomEvent('toggleSearch');
        window.dispatchEvent(closeSearchEvent);
    };

    return (
        <>
            <Input placeholder={props.inputPlaceholder}
                   visible={state.searchInputVisible}
                   insideHeader={true}
                   buttonLabel={props.buttonLabel}
                   searchButtonAlt={props.searchButtonAlt}
                   searchNavAlt={props.searchNavAlt}
                   searchCancelAlt={props.searchCancelAlt}
                   autocompleteResultProducts={state.autocompleteResultProducts}
                   autocompleteResultPages={state.autocompleteResultPages}
                   query={state.query}
                   onChange={(query) => changeQueryAndSearch(query)}
                   onBlur={toggleSearch}
                   renderStatic={props.renderStatic === 'true'}
                   onKeyUp={(query) => getAutocomplete(query)}/>
            {!(props.renderStatic === 'true')
                && <a ref={linkRef} onKeyDown={toggleSearch} onClick={toggleSearch} role="button"
                      aria-label={props.searchNavAlt} tabIndex={0}
                      className="magenta-icon-search search-icon tooltip" data-tooltip={props.buttonLabel}>
                    <span>{props.buttonLabel}</span>
                </a>
            }
        </>
    );
};

export default Container;
